import { Injectable } from '@angular/core';
import { Point, Feature, FeatureCollection, Geometry } from 'geojson';
import { Observable, forkJoin, of } from 'rxjs';
import { TimeSeries, MetadataService, OpendapService, UTCDate } from 'map-wald';
import { map, shareReplay, switchAll, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AggregationFunction, LayerDescriptor, MetadataConfig, TimePeriod } from './data';
import { DapData, DapDAS, DapDDX, DapVariableData } from 'dap-query-js/dist/dap-query';
import { HttpClient } from '@angular/common/http';
import { transpose } from 'ramda';
import { TimePeriodUtils } from './utils';

const DEFAULT_ID_COLUMN = 'ID';

export interface FeatureDataConfig extends LayerDescriptor {
  id?:string;
  skipGeometry?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureDataService {
  private layerCache: { [key: string]: Observable<FeatureCollection<Point>> } = {};
  private dapCache: { [key: string]: Observable<DapData> } = {};
  lookups$: Observable<{ [key: string]: any }>;
  constructor(http:HttpClient, private metadata: MetadataService, private dap: OpendapService) {
    this.lookups$ = http.get(environment.attributeTranslations).pipe(shareReplay()) as Observable<{ [key: string]: any }>;
  }

  static nestedLayers(layer:LayerDescriptor):LayerDescriptor[]{
    if(!layer.combine){
      return [layer]
    }

    const variants = layer.combine.map(c=>layer.variants.find(v=>v.variantLabel===c));
    return variants.map(v=>{
      const result = {...layer,...v};
      delete result.combine;
      return result;
    });
  }

  getFeatures(layer: FeatureDataConfig,filter?:{[key:string]:any}): Observable<FeatureCollection<Point>> {
    if(layer.combine){
      const layersToCombine = FeatureDataService.nestedLayers(layer).map(l=>this.getFeatures(l,filter));
      return forkJoin(layersToCombine).pipe(map(concatCoverages));
    }

    const res$ = of(layer).pipe(
      map(lyr => {
        if (!this.layerCache[lyr.filename]) {
          this.layerCache[lyr.filename] = this._retrieveLayer(lyr);
        }
        return this.layerCache[lyr.filename];
      }),
      switchAll()
    );

    if(!filter){
      return res$;
    }

    return res$.pipe(
      map(fc=>{
        return {
          type: 'FeatureCollection',
          features: fc.features.filter(f=>{
            return Object.keys(filter).every(k=>{
              return f.properties[k]===filter[k];
            });
          })
        };
      }));
  }

  getTimes(layer: FeatureDataConfig): Observable<UTCDate[]> {
    if(layer.combine){
      return forkJoin(FeatureDataService.nestedLayers(layer).map(l=>this.getTimes(l))).pipe(
        map(arrays=>arrays.reduce((acc,cur)=>acc.concat(cur),[])),
        map(arr=>arr.sort((a,b)=>a.getTime()-b.getTime())),
        map(arr=>arr.filter((v,i,a)=>!i||v.getTime()!==a[i-1].getTime()))
      );
    }

    return of(layer).pipe(
      map(cfg=>{
        if(!cfg.filename){
          return of(null);
        }
        const url = `${environment.tds}/dodsC/${cfg.filename}`;
        return this.metadata.getTimeDimensionForURL(url)
      }),
      switchAll()
    );
  }

  private _closestTimeIndex(date:UTCDate, dates:UTCDate[]):number {
    const dateT = date.getTime();
    const deltas = dates.map(t => Math.abs(t.getTime() - dateT));
    const closest = deltas.indexOf(Math.min(...deltas));
    return closest;
  }

  private _coverageQuery(layer:FeatureDataConfig,variable?:string){
    let result$ = this.getFeatures(layer).pipe(
      map((features)=>{
        const config = layer;
        variable = variable || config.variable || config.variables[0];
        return {
          features,
          variable,
          config,
          url: ''
        };
      }),
      map(query=>{
        query.url = `${environment.tds}/dodsC/${query.config.filename}`;
        return forkJoin([
          this.metadata.dasForUrl(query.url),
          this.metadata.getTimeDimensionForURL(query.url),
          of(query)
        ]);
      }),
      switchAll(),
      map(([das,timeDim,query])=>Object.assign({},query,{das,timeDim}))
    );
    return result$;
  }

  getAggregateValues(layer:FeatureDataConfig, filter:{[key:string]:any}, timeperiod: TimePeriod, variable?: string, keepNulls?: boolean,aggregation?:AggregationFunction): Observable<FeatureCollection<Point>>{
    if(layer.combine){
      return forkJoin(FeatureDataService.nestedLayers(layer).map(l=>this.getAggregateValues(l,filter,timeperiod,variable,keepNulls,aggregation))).pipe(
        map(concatCoverages));
    }
    return this._coverageQuery(layer,variable).pipe(
      map((query)=>{
        const featureRange = this.dap.dapRangeQuery(0,query.features.features.length-1);
        const withinPeriod = query.timeDim.map(d=>TimePeriodUtils.withinTimePeriod(d,timeperiod));
        const startTimeIdx = withinPeriod.indexOf(true);
        const endTimeIdx = withinPeriod.lastIndexOf(true)
        let data$:Observable<DapData>;
        if((startTimeIdx<0)||(endTimeIdx<0)){
          data$ = of(null);
        } else {
          const timeQuery =  this.dap.dapRangeQuery(startTimeIdx,endTimeIdx);
          const constraint = query.config.timeFirst ?
            `${timeQuery}${featureRange}`:
            `${featureRange}${timeQuery}`;

          data$ = this.getDAP(`${query.url}.ascii?${query.variable}${constraint}`,query.das);
        }
        return forkJoin([
          data$,
          of(query)
        ]);
      }),
      switchAll(),
      map(([data,query])=>{
        const reducer = (arr:number[]) =>{
          if(arr?.length===undefined){
            return arr as any as number;
          }
          const vals = arr.filter(v=>(v!==null)&&(v!==undefined));

          if(vals.length===0){
            return null;
          }

          switch(aggregation){
            case 'mean':
              return vals.reduce((a,b)=>a+b,0)/vals.length;
            case 'min':
              return Math.min(...vals);
            case 'max':
              return Math.max(...vals);
            default:
              return vals.reduce((a,b)=>a+b,0);
          }
        }
        return this.makeCoverage(query.features,data,query.config.timeFirst?1:0,layer.id,query.variable,reducer,keepNulls);
      }));
    }

  private makeCoverage(features: FeatureCollection<Point, { [name: string]: any; }>,
               data: DapData, featureDim:number, idCol:string, variable:string, reducer: (val: any) => number, keepNulls: boolean): any {
    const result:FeatureCollection<Point> = {
      type:'FeatureCollection',
      features:[]
    };
    result.features = features.features.map(f=>{
      const newF: Feature<Point> = {
        type: 'Feature',
        geometry:f.geometry,
        properties:{...f.properties}
      };
      idCol = idCol||DEFAULT_ID_COLUMN;
      if(!data||!data[idCol]){
        newF.properties.value = null;
      } else {
        const idx = (data[idCol] as number[]).indexOf(newF.properties[idCol]);
        if(featureDim===0||(((data[variable] as any[])[0])?.length===undefined)){
          newF.properties.value = reducer((data[variable] as any[])[idx]);
        } else if(featureDim===1){
          newF.properties.value = reducer((data[variable] as any[][]).map(arr=>arr[idx]));
        }
      }

      // if(keepNulls){
      //   newF.properties.value = newF.properties.value || 0.0;
      // }
      return newF;
    }).filter(f=>keepNulls||f.properties.value!==null)
    return result;
  }

  private getDAP(url:string,das:DapDAS):Observable<DapData>{
    if(!this.dapCache[url]){
      this.dapCache[url] = this.dap.getData(url,das).pipe(shareReplay());
    }
    return this.dapCache[url];
  }

  getTimeSeries(layer:FeatureDataConfig,feature:Feature,variable?:string):Observable<TimeSeries>{
    if(layer.combine){
      return forkJoin(FeatureDataService.nestedLayers(layer).map(l=>this.getTimeSeries(l,feature,variable))).pipe(
        map(series=>series.filter(s=>s!==null)[0]),
      );
    }
    const res$ = this.getFeatures(layer).pipe(
      map(features=>{
        const config:FeatureDataConfig = layer;
        variable = variable || config.variable || config.variables[0];
        const idCol = layer.id||DEFAULT_ID_COLUMN;
        return {
          variable,
          config,
          idx: features.features.findIndex(f=>f.properties[idCol]===feature.properties[idCol]),
          url: ''
        };
      }),
      map(query=>{
        if(query.idx<0){
          return of(null);
        } else {
          query.url = `${environment.tds}/dodsC/${query.config.filename}`;
          return forkJoin([this.metadata.dasForUrl(query.url), this.metadata.ddxForUrl(query.url), of(query)]).pipe(
            map(([das,ddx,query])=>{
              const range = this.dap.dapRangeQuery(query.idx);

              let dateRange = '';
              if(query.config.timeFirst){
                const timeSize = +ddx.variables[query.config.time].dimensions[0].size;
                dateRange = this.dap.dapRangeQuery(0,timeSize-1);
              }
              const url = `${query.url}.ascii?${query.variable}${dateRange}${range}`;
              return forkJoin([this.getDAP(url,das),of(query)]);
            }),
            switchAll(),
            map(([data,query])=>{
              const vals = data[query.variable] as number[];
              return {
                dates:data[query.config.time] as UTCDate[],
                values:vals
              };
            }),
            map(ts=>{
              return {
                dates:ts.dates.filter((_,i)=>!isNaN(ts.values[i])&&(ts.values[i]!==null)),
                values:ts.values.filter(v=>!isNaN(v)&&(v!==null))
              };
            })
          );
        }
      }),
      switchAll(),
    );
    return res$;
  }

  private _metaFromFiles(metaConfig: MetadataConfig,idCol:string):Observable<{[key:string]:number[]}>{
    let variables = metaConfig.meta || [];
    const url = `${environment.tds}/dodsC/${metaConfig.filename}`;

    let res$ = forkJoin([this.metadata.dasForUrl(url),this.metadata.ddxForUrl(url)]).pipe(
      map(([das,ddx]) => {
        return forkJoin(variables.map(v => {
          const ddxRecord = (ddx as DapDDX).variables[v];
          if(!ddxRecord){
            return of(null as DapData);
          }
          const retrieval$ = this.getDAP(`${url}.ascii?${v}`, das as DapDAS);

          if((ddx as DapDDX).variables[v].dimensions[0].name===idCol){
            return retrieval$;
          }
          return retrieval$.pipe(
            map(data => {
              const result = Object.assign({},data);
              result[v] = transpose(data[v] as number[][]);
              return result;
            }));
        }));
      }),
      switchAll(),
      map(data=>{
        const result:{[key:string]:number[]} = {};
        data.forEach((d,i)=>{
          if(!d){
            return;
          }
          result[variables[i]] = d[variables[i]] as number[];
        })
        return result;
      }));
    return res$;
  }

  private _retrieveLayer(lyr: FeatureDataConfig): Observable<FeatureCollection<Point>> {
    const idCol = lyr.id||DEFAULT_ID_COLUMN;
    const coreMeta:MetadataConfig = {
      filename: lyr.filename,
      meta: ([] as string[]).concat(lyr.meta||[],[idCol,'latitude','longitude'])
    }

    let allMeta = [coreMeta];
    allMeta = allMeta.concat(lyr.relatedFiles||[]);

    let meta$ = forkJoin(allMeta.map(m=>this._metaFromFiles(m,idCol))).pipe(
      map(allMeta=>Object.assign({},...allMeta)));

    return forkJoin([meta$,this.lookups$]).pipe(
      map(metaAndLookups=>{
        return {
          meta:metaAndLookups[0],
          lookups:metaAndLookups[1]
        };
      }),
      map(metaAndLookups => {
        const data = metaAndLookups.meta;
        const lookups = metaAndLookups.lookups;
        const result: FeatureCollection<Point> = {
          type: 'FeatureCollection',
          features: []
        };

        result.features = (data[idCol] as any[]).map((_,i)=>{
          const f: Feature<Point> = {
            type: 'Feature',
            geometry: null,
            properties:{}
          };
          if(!lyr.skipGeometry){
            f.geometry = {
              type: 'Point',
              coordinates: [data.longitude[i],data.latitude[i]],
            };
          }

          Object.keys(data).filter(v=>(v!=='latitude')&&(v!=='longitude')).forEach(v=>{
            let val = data[v][i];
            if(lookups[v]&&lookups[v][val]){
              val = lookups[v][val];
            }
            f.properties[v] = val;
          });

          f.properties.source = (lyr as any).variantLabel;
          f.properties.chartLabel = lyr.chartLabel;
          f.properties.rankWeight = lyr.rankWeight || 1.0;;

          return f;
        });
        return result;
      }));
  }
}

function concatCoverages<T extends Geometry>(features:FeatureCollection<T>[]):FeatureCollection<T>{
  return {
    type: 'FeatureCollection',
    features: features.reduce((a,b)=>a.concat(b.features),[])
  };
}

